import { Icon, Link, type LinkProps } from '@chakra-ui/react'
import { ExternalLinkIcon } from 'lucide-react'
import type { PropsWithChildren } from 'react'

type ExternalLinkProps = PropsWithChildren<
  Omit<LinkProps, 'href'> & { href: string }
>

/**
 * Ensures URL has a protocol (defaults to https://)
 * This prevents URLs without protocols from being treated as internal routes
 */
export function ensureProtocol(url: string): string {
  // If URL already has a protocol, return as-is
  if (url.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
    return url
  }

  // Otherwise, add https:// prefix
  return `https://${url}`
}

export function ExternalLink({ children, href, ...props }: ExternalLinkProps) {
  return (
    // Presentation comes before the spread so callers can restyle; the
    // security-relevant attributes come after it so they cannot. Dropping
    // `rel` while keeping `target="_blank"` would hand the opened tab a
    // `window.opener` back into this app.
    <Link
      color="blue.500"
      display="flex"
      alignItems="center"
      gap={0.5}
      {...props}
      href={ensureProtocol(href)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
      <Icon
        as={ExternalLinkIcon}
        w={3}
        h={3}
        ml={0.5}
        display="inline-block"
        data-testid="external-link-icon"
      />
    </Link>
  )
}
